import React, { useId } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import type { ServiceProofFootnote, ServiceProofTile } from './ServiceLayout';
import { darkCardSurface } from './bandedStyles';
import BandDisclosure from './BandDisclosure';

/**
 * The dark proof band under the hero (banded service layout).
 *
 * Same recipe as the pricing page's estimate card: a radial Davinci Blue glow
 * over Midnight. Claims rule, kept exactly: every tile carries its own period in
 * the label (the `proofPoints.ts` text), so the figure is never shown without
 * its basis. Only the long definitions fold into a "How we measure these"
 * disclosure. They stay in the DOM when closed (hidden with `visibility`, the
 * same pattern as the FAQ answers), so they are crawlable and are not reached
 * by Tab until opened.
 */
interface ProofBandProps {
  heading: string;
  /** One line of basis, shown first inside the "How we measure these" panel, e.g. "Client tickets Apr-Sep 2026". */
  basis?: string;
  /** A link at the end of the panel to the full definitions, e.g. /how-we-measure. */
  moreLink?: { text: string; href: string };
  tiles: ServiceProofTile[];
  footnotes?: ServiceProofFootnote[];
  disclosureLabel?: string;
}

const Band = styled.section`
  ${darkCardSurface}
  z-index: 2;

  /* The band's own glow, as built: slightly wider and softer than the estimate card's. */
  &::before {
    background:
      radial-gradient(110% 90% at 90% -20%, rgba(11, 152, 206, 0.5), transparent 60%),
      linear-gradient(180deg, var(--davinci-midnight), rgb(16, 42, 79));
  }
`;

const Inner = styled.div`
  padding: 24px 16px 14px;

  @media (min-width: 768px) {
    padding: 24px 32px 16px;
  }
`;

const Top = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: var(--font-weight-bold);
  line-height: var(--lh-snug);
  color: var(--white);
`;

/* Claims basis is never smaller than 13px on a phone (round 29, E05). */
const Basis = styled.p`
  margin: 0;
  padding: 2px 0 8px;
  font-size: max(13px, var(--fs-xs));
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.86);
`;

const MoreLink = styled(Link)`
  display: inline-block;
  margin: 0 0 12px;
  font-size: max(13px, var(--fs-xs));
  font-weight: var(--font-weight-bold);
  color: var(--davinci-beach);
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    outline: 3px solid var(--davinci-beach);
    outline-offset: 2px;
  }
`;

const Tiles = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;

  @media (min-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
  }
`;

const TileItem = styled.li`
  margin: 0;
  padding: 16px 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);

  .value {
    display: block;
    font-size: clamp(2rem, 6.2vw, 3rem);
    font-weight: var(--font-weight-extra-bold);
    letter-spacing: -0.02em;
    line-height: 1;
    color: var(--white);
  }

  .label {
    display: block;
    margin-top: 0.45rem;
    font-size: max(14px, var(--fs-xs));
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.86);
  }

  sup {
    margin-left: 2px;
    font-size: 12px;
    line-height: 0;
    font-weight: var(--font-weight-bold);
    color: var(--davinci-beach);
  }

  @media (min-width: 900px) {
    padding: 6px 22px;
    border: 0;
    border-left: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 0;
    background: none;

    &:first-child {
      padding-left: 0;
      border-left: 0;
    }
  }
`;

const Definitions = styled.ol`
  margin: 0;
  padding: 2px 0 12px 1.2rem;
  font-size: max(13px, var(--fs-xs));
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.86);

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: var(--white);
  }
`;

const ProofBand: React.FC<ProofBandProps> = ({
  heading,
  basis,
  moreLink,
  tiles,
  footnotes,
  disclosureLabel = 'How we measure these',
}) => {
  const headingId = `${useId()}-heading`;

  return (
    <Band aria-labelledby={headingId}>
      <Inner>
        <Top>
          <Heading id={headingId}>{heading}</Heading>
        </Top>
        <Tiles>
          {tiles.map((tile, index) => (
            <TileItem key={tile.label}>
              <span className="value">{tile.value}</span>
              <span className="label">
                {tile.label}
                {footnotes?.[index] && <sup>{index + 1}</sup>}
              </span>
            </TileItem>
          ))}
        </Tiles>
        {footnotes && footnotes.length > 0 && (
          <BandDisclosure label={disclosureLabel}>
            {basis && <Basis>{basis}</Basis>}
            <Definitions>
              {footnotes.map((note) => (
                <li key={note.term}>
                  <strong>{note.term}.</strong> {note.text}
                </li>
              ))}
            </Definitions>
            {moreLink && <MoreLink to={moreLink.href}>{moreLink.text}</MoreLink>}
          </BandDisclosure>
        )}
      </Inner>
    </Band>
  );
};

export default ProofBand;
